import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, ArrowLeft, Loader2, Send, ChevronRight, AlertTriangle,
  Clock, Scale, Briefcase, FileText, User, Calendar, Zap,
  CheckCircle, XCircle, Shield, Tag, ExternalLink, RefreshCw,
  MessageSquare, Plus, History, Search, Mail, Flag, Info,
  Sparkles, Target, ListChecks, PenLine, Phone, FileSearch,
  AlertCircle, BookOpen, Lightbulb, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PRE-CASE THREAD (Layer 2) - "Senior-kollega" opplevelse
 * 
 * Ikke en rapport-side, men en arbeidsflate der AI allerede har
 * "tenkt ferdig første runde" og gir konkrete faglige anbefalinger.
 */
const PreCaseThread = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analysis');
  
  // Chat state - med seeded første melding
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  
  // Draft state
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [draft, setDraft] = useState(null);
  
  // Case creation
  const [creatingCase, setCreatingCase] = useState(false);
  const [showCaseForm, setShowCaseForm] = useState(false);
  const [caseForm, setCaseForm] = useState({ topic: '', case_type: 'OTHER', description: '' });

  useEffect(() => {
    if (threadId) fetchThread();
  }, [threadId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchThread = async () => {
    setLoading(true);
    try {
      const token = auth.getToken();
      const response = await axios.get(
        `${API}/outlook/precase-threads/${threadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const threadData = response.data;
      setThread(threadData);
      
      // Pre-fill case form
      const analysis = threadData.deep_analysis || {};
      setCaseForm({
        topic: analysis.suggested_case_title || '',
        case_type: analysis.suggested_case_type || 'OTHER',
        description: analysis.summary || ''
      });
      
      // Seed chat med intelligent første melding basert på analysen
      seedChatMessage(threadData);
      
    } catch (error) {
      console.error('Error fetching thread:', error);
      toast.error('Kunne ikke laste tråden');
    } finally {
      setLoading(false);
    }
  };

  const seedChatMessage = (threadData) => {
    const analysis = threadData?.deep_analysis || {};
    const emailData = threadData?.email_data || {};
    const nextSteps = analysis.next_steps || [];
    const hasActions = nextSteps.length > 0;
    
    // Bygg en naturlig, handlingsorientert første melding
    let seedMessage = `Jeg har gjennomgått henvendelsen fra ${emailData.from?.name || 'klienten'}. `;
    
    if (analysis.risk_level === 'HIGH') {
      seedMessage += `**Dette er en høyrisiko-sak** som krever rask oppfølging. `;
    }
    
    seedMessage += `\n\n**Min vurdering:** ${analysis.summary || 'Se analyse over.'}\n\n`;
    
    if (hasActions) {
      seedMessage += `**Hva vil du gjøre videre?**\n`;
      seedMessage += `- Skal jeg lage et **svarutkast** med avklaringsspørsmålene?\n`;
      seedMessage += `- Vil du at jeg **oppretter saken** med foreslått mal?\n`;
      seedMessage += `- Har du spørsmål om risikovurderingen?`;
    } else {
      seedMessage += `Hva vil du at jeg skal gjøre videre med denne henvendelsen?`;
    }
    
    setChatMessages([{ role: 'assistant', content: seedMessage, id: 'seed-1' }]);
  };

  const sendMessage = async () => {
    if (!chatInput.trim() || sending) return;
    
    const message = chatInput.trim();
    setChatInput('');
    setSending(true);
    
    // Optimistic update
    const tempId = `temp-${Date.now()}`;
    setChatMessages(prev => [...prev, { role: 'user', content: message, id: tempId }]);
    
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/outlook/precase-threads/${threadId}/messages`,
        { content: message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update med ekte meldinger
      setChatMessages(prev => [
        ...prev.filter(m => m.id !== tempId),
        response.data.user_message,
        response.data.assistant_message
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Kunne ikke sende melding');
      setChatMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  const generateDraft = async () => {
    setGeneratingDraft(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/outlook/precase-threads/${threadId}/generate-draft`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft(response.data.draft);
      toast.success('Svarutkast generert');
    } catch (error) {
      console.error('Error generating draft:', error);
      toast.error('Kunne ikke generere utkast');
    } finally {
      setGeneratingDraft(false);
    }
  };

  const createCase = async () => {
    if (!caseForm.topic.trim()) {
      toast.error('Sakstittel er påkrevd');
      return;
    }
    
    setCreatingCase(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/outlook/precase-threads/${threadId}/convert-to-case`,
        caseForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Sak opprettet!');
      const caseId = response.data.case?.id;
      if (caseId) {
        navigate(`/lexy/cases/${caseId}`);
      } else {
        navigate('/lexy/dashboard?tab=cases');
      }
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Kunne ikke opprette sak');
    } finally {
      setCreatingCase(false);
    }
  };

  // Quick action handlers
  const handleQuickAction = (action) => {
    switch (action.action_type) {
      case 'create_draft':
        generateDraft();
        break;
      case 'create_case':
        setShowCaseForm(true);
        break;
      case 'conflict_check':
        setChatInput('Kan du kjøre en konfliktsjekk for denne henvendelsen?');
        setActiveTab('chat');
        break;
      case 'clarification':
        setChatInput(`Kan du lage et svarutkast som spør klienten om: ${action.description}`);
        setActiveTab('chat');
        break;
      default:
        setChatInput(action.description);
        setActiveTab('chat');
    }
  };

  // Helpers
  const getPriorityColor = (p) => ({ 'URGENT': 'bg-red-500', 'HIGH': 'bg-orange-500', 'NORMAL': 'bg-emerald-500', 'LOW': 'bg-gray-400' }[p] || 'bg-gray-400');
  const getRiskColor = (r) => ({ 'HIGH': 'text-red-700 bg-red-50 border-red-200', 'MEDIUM': 'text-amber-700 bg-amber-50 border-amber-200', 'LOW': 'text-emerald-700 bg-emerald-50 border-emerald-200' }[r] || 'text-gray-600 bg-gray-50');
  const getRiskLabel = (r) => ({ 'HIGH': 'Høy risiko', 'MEDIUM': 'Middels risiko', 'LOW': 'Lav risiko' }[r] || r);
  const getPriorityLabel = (p) => ({ 'URGENT': 'Haster', 'HIGH': 'Høy', 'NORMAL': 'Normal', 'LOW': 'Lav' }[p] || p);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Brain className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-gray-600 font-medium">Analyserer henvendelsen...</p>
          <p className="text-sm text-gray-400 mt-1">AI gjennomgår e-posten</p>
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Tråden ble ikke funnet</p>
          <button type="button" onClick={() => navigate('/lexy/ai-workspace')} className="text-violet-600 hover:text-violet-700 font-medium">
            Tilbake til AI-arbeidsflate
          </button>
        </div>
      </div>
    );
  }

  const analysis = thread.deep_analysis || {};
  const emailData = thread.email_data || {};
  const similarCases = analysis.similar_cases || [];
  const nextSteps = analysis.next_steps || [];
  // Bruk konsistent confidence fra analysen
  const confidence = Math.round((analysis.confidence || 0.5) * 100);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Pre-case Banner */}
      <div className="flex-shrink-0 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5" />
            <span className="font-medium">Pre-case analyse</span>
            <span className="text-violet-200 text-sm">• Ingen sak opprettet ennå</span>
          </div>
          <button
            onClick={() => setShowCaseForm(true)}
            className="px-4 py-1.5 bg-white text-violet-600 rounded-lg font-medium text-sm hover:bg-violet-50 transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Opprett sak
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/lexy/ai-workspace')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-gray-900 truncate">{emailData.subject || 'Ny henvendelse'}</h1>
            <p className="text-sm text-gray-500">Fra: {emailData.from?.name || emailData.from?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(analysis.priority)} text-white`}>
              {getPriorityLabel(analysis.priority)}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getRiskColor(analysis.risk_level)}`}>
              {getRiskLabel(analysis.risk_level)}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto flex gap-1 px-4">
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition ${activeTab === 'analysis' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Lightbulb className="w-4 h-4 inline mr-2" />
            AI Vurdering
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition ${activeTab === 'chat' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Arbeidsrom
            {chatMessages.length > 1 && (
              <span className="ml-1 px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded text-xs">
                {chatMessages.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'analysis' ? (
          <div className="max-w-6xl mx-auto p-6">
            {/* === SENIOR BRIEF - Hovedseksjonen === */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 mb-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-6 h-6 text-violet-300" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-lg font-semibold">AI Senior-vurdering</h2>
                    <span className="px-2 py-0.5 bg-white/20 rounded text-xs">{confidence}% sikkerhet</span>
                  </div>
                  
                  {/* Kategori og type */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-violet-500/30 rounded-lg text-sm font-medium">
                      ⚖️ {analysis.category}
                    </span>
                    {analysis.subcategory && (
                      <span className="text-violet-300 text-sm">{analysis.subcategory}</span>
                    )}
                  </div>
                  
                  {/* Hovedvurdering - den viktigste teksten */}
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {analysis.summary}
                  </p>
                  
                  {/* Risiko og frister - inline */}
                  <div className="flex flex-wrap gap-3">
                    {analysis.risk_level === 'HIGH' && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-lg border border-red-400/30">
                        <AlertTriangle className="w-4 h-4 text-red-300" />
                        <span className="text-sm text-red-200">Høyrisiko-sak</span>
                      </div>
                    )}
                    {analysis.deadlines?.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 rounded-lg border border-amber-400/30">
                        <Clock className="w-4 h-4 text-amber-300" />
                        <span className="text-sm text-amber-200">{analysis.deadlines[0]}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* === ANBEFALTE HANDLINGER - Faglige actions === */}
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 mb-6">
              <h3 className="font-semibold text-violet-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Anbefalte handlinger
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Primær handling: Lag svarutkast */}
                <button
                  onClick={generateDraft}
                  disabled={generatingDraft}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-violet-200 hover:border-violet-400 transition text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 transition">
                    {generatingDraft ? <Loader2 className="w-5 h-5 text-violet-600 animate-spin" /> : <PenLine className="w-5 h-5 text-violet-600" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Lag svarutkast</p>
                    <p className="text-sm text-gray-500">Med avklaringsspørsmål til klient</p>
                  </div>
                </button>
                
                {/* Opprett sak */}
                <button
                  onClick={() => setShowCaseForm(true)}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-violet-200 hover:border-violet-400 transition text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Opprett sak</p>
                    <p className="text-sm text-gray-500">Med foreslått mal og data</p>
                  </div>
                </button>
                
                {/* Dynamiske actions fra analysen */}
                {nextSteps.filter(s => s.action_type !== 'create_case').slice(0, 2).map((step, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickAction(step)}
                    className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-violet-300 transition text-left group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition">
                      {step.action_type === 'conflict_check' ? <Shield className="w-5 h-5 text-gray-600" /> :
                       step.action_type === 'clarification' ? <MessageSquare className="w-5 h-5 text-gray-600" /> :
                       <FileText className="w-5 h-5 text-gray-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{step.action}</p>
                      <p className="text-sm text-gray-500 line-clamp-1">{step.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generert utkast */}
            {draft && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Svarutkast generert
                  </h3>
                  <button
                    onClick={() => navigator.clipboard.writeText(draft)}
                    className="text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    Kopier
                  </button>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-100 text-sm text-gray-700 whitespace-pre-wrap">
                  {draft}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Venstre kolonne - Detaljer */}
              <div className="lg:col-span-2 space-y-6">
                {/* Hva vi vet / Hva mangler */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Bekreftet informasjon
                    </h4>
                    <ul className="space-y-2">
                      {(analysis.what_we_know || []).map((item, i) => (
                        <li key={i} className="text-sm text-emerald-700 flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Må avklares
                    </h4>
                    <ul className="space-y-2">
                      {(analysis.what_is_missing || []).map((item, i) => (
                        <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Foreslåtte spørsmål til klient */}
                {analysis.clarification_questions?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                    <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Spørsmål til klienten
                    </h4>
                    <ul className="space-y-2">
                      {analysis.clarification_questions.map((q, i) => (
                        <li key={i} className="text-sm text-blue-700 flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center flex-shrink-0 text-xs font-medium">{i + 1}</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Høyre kolonne - Sidebar */}
              <div className="space-y-6">
                {/* Historikk - KUN hvis det finnes treff */}
                {similarCases.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Relevant historikk
                    </h4>
                    <div className="space-y-3">
                      {similarCases.map((c, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-900">{c.topic}</p>
                          <p className="text-xs text-violet-600 mt-1">
                            {c.similarity_reason}
                          </p>
                        </div>
                      ))}
                    </div>
                    {analysis.historical_context && (
                      <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
                        {analysis.historical_context}
                      </p>
                    )}
                  </div>
                )}

                {/* Risikofaktorer - kun hvis høy/medium risiko */}
                {analysis.risk_factors?.length > 0 && analysis.risk_level !== 'LOW' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                    <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Risikofaktorer
                    </h4>
                    <ul className="space-y-1">
                      {analysis.risk_factors.map((r, i) => (
                        <li key={i} className="text-sm text-red-700">• {r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Foreslått sak-info */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Forslått sak
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Tittel:</span> <span className="text-gray-900">{analysis.suggested_case_title || 'Ikke satt'}</span></p>
                    <p><span className="text-gray-500">Type:</span> <span className="text-gray-900">{analysis.suggested_case_type || 'OTHER'}</span></p>
                    {analysis.suggested_case_template && (
                      <p><span className="text-gray-500">Mal:</span> <span className="text-gray-900">{analysis.suggested_case_template}</span></p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* === CHAT TAB - Seeded og handlingsorientert === */
          <div className="max-w-3xl mx-auto p-6">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              {/* Chat Messages */}
              <div className="h-[500px] overflow-y-auto p-4 space-y-4 bg-gray-50">
                {chatMessages.map((msg, i) => (
                  <div key={msg.id || i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center mr-2 flex-shrink-0">
                        <Brain className="w-4 h-4 text-violet-600" />
                      </div>
                    )}
                    <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${msg.role === 'user' ? 'bg-violet-600 text-white rounded-br-md' : 'bg-white border border-gray-200 text-gray-700 rounded-bl-md shadow-sm'}`}>
                      <div 
                        className={`text-sm whitespace-pre-wrap ${msg.role === 'user' ? '' : 'prose prose-sm max-w-none'}`}
                        dangerouslySetInnerHTML={{ 
                          __html: msg.content
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\n/g, '<br/>') 
                        }}
                      />
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center mr-2">
                      <Brain className="w-4 h-4 text-violet-600" />
                    </div>
                    <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                      <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              
              {/* Quick actions */}
              <div className="px-4 py-2 bg-white border-t border-gray-100">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setChatInput('Lag et svarutkast til klienten')}
                    className="px-3 py-1.5 text-xs bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition"
                  >
                    Lag svarutkast
                  </button>
                  <button
                    onClick={() => setChatInput('Hva er de største risikoene her?')}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                  >
                    Risikoanalyse
                  </button>
                  <button
                    onClick={() => setChatInput('Hvilke dokumenter trenger vi?')}
                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                  >
                    Dokumentbehov
                  </button>
                  <button
                    onClick={() => setShowCaseForm(true)}
                    className="px-3 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition"
                  >
                    Opprett sak
                  </button>
                </div>
              </div>
              
              {/* Chat Input */}
              <div className="border-t border-gray-200 p-4 bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder="Spør AI om noe..."
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!chatInput.trim() || sending}
                    className="px-5 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-50 transition flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Case Creation Modal */}
      <AnimatePresence>
        {showCaseForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowCaseForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Opprett sak</h2>
                  <p className="text-sm text-gray-500">Fra pre-case analyse</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sakstittel *</label>
                  <input
                    type="text"
                    value={caseForm.topic}
                    onChange={e => setCaseForm(prev => ({ ...prev, topic: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sakstype</label>
                  <select
                    value={caseForm.case_type}
                    onChange={e => setCaseForm(prev => ({ ...prev, case_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="EMPLOYMENT">Arbeidsrett</option>
                    <option value="CONTRACT">Kontrakt</option>
                    <option value="FAMILY">Familie</option>
                    <option value="PROPERTY">Eiendom</option>
                    <option value="CORPORATE">Selskapsrett</option>
                    <option value="CRIMINAL">Straff</option>
                    <option value="OTHER">Annet</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCaseForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Avbryt
                </button>
                <button
                  onClick={createCase}
                  disabled={creatingCase || !caseForm.topic.trim()}
                  className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
                >
                  {creatingCase ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Opprett
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PreCaseThread;
